import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, Send } from "lucide-react";
import { useLang, WHATSAPP_URL, PHONE_DISPLAY } from "../i18n";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-700/70 bg-slate-900/60 px-4 py-3 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20";

export const ContactSection = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", type: 0, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.final.f.required);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          business: form.business,
          phone: form.phone,
          email: form.email || null,
          business_type: t.final.f.types[form.type],
          language: lang,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(t.final.f.ok);
      setForm({ name: "", business: "", phone: "", email: "", type: 0, message: "" });
    } catch {
      toast.error(t.final.f.err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="grid-lines absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-96 w-[54rem] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal className="text-center">
          <h2 className="font-display mx-auto max-w-4xl text-4xl font-black leading-[1.08] tracking-tight text-slate-50 sm:text-6xl">
            {t.final.title}
          </h2>
          <p className="mt-5 text-lg text-slate-400 sm:text-xl">
            <span className="text-gradient font-bold">2 CHF</span> {t.final.sub.replace("2 CHF", "").trim()}
          </p>
          <div className="mt-10 flex flex-col items-center gap-5">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-whatsapp-btn"
              className="inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-10 py-5 text-lg font-bold text-slate-950 shadow-[0_0_60px_rgba(16,185,129,0.45)] transition-transform hover:scale-105"
            >
              <WhatsAppIcon className="h-6 w-6" />
              {t.final.cta}
            </a>
            <a
              href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
              data-testid="final-phone-link"
              className="inline-flex items-center gap-2.5 font-mono2 text-xl tracking-wider text-slate-200 transition-colors hover:text-emerald-300 sm:text-2xl"
            >
              <Phone className="h-5 w-5 text-emerald-400" />
              {PHONE_DISPLAY}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="glass mx-auto mt-20 max-w-3xl rounded-3xl p-7 sm:p-10"
          >
            <h3 className="font-display text-2xl font-bold tracking-tight text-slate-50">{t.final.formTitle}</h3>
            <p className="mt-2 text-sm text-slate-400">{t.final.formSub}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.name} *</label>
                <input id="cf-name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
              </div>
              <div>
                <label htmlFor="cf-business" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.business}</label>
                <input id="cf-business" data-testid="contact-business-input" className={inputCls} value={form.business} onChange={set("business")} />
              </div>
              <div>
                <label htmlFor="cf-phone" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.phone} *</label>
                <input id="cf-phone" data-testid="contact-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} required />
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.email}</label>
                <input id="cf-email" data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-type" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.type}</label>
                <select
                  id="cf-type"
                  data-testid="contact-type-select"
                  className={inputCls}
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: Number(e.target.value) })}
                >
                  {t.final.f.types.map((ty, i) => (
                    <option key={i} value={i} className="bg-slate-900">{ty}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-1.5 block text-xs font-medium text-slate-400">{t.final.f.message}</label>
                <textarea id="cf-message" data-testid="contact-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-8 py-4 text-base font-bold text-slate-950 transition-transform hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? t.final.f.sending : t.final.f.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
